import React, { useState } from 'react';
import { X, Loader2, UserPlus } from 'lucide-react';
import { validate } from '../../utils/validation';

interface CreateUserModalProps {
    isOpen: boolean;
    onClose: () => void;
    onSubmit: (userData: {
        firstName: string;
        lastName: string;
        email: string;
        role: string;
    }) => Promise<void>;
    isCreating: boolean;
}

const CreateUserModal: React.FC<CreateUserModalProps> = ({
    isOpen,
    onClose,
    onSubmit,
    isCreating
}) => {
    const [formData, setFormData] = useState({
        firstName: '',
        lastName: '',
        email: '',
        role: 'user',
    });

    const [errors, setErrors] = useState<{
        firstName?: string;
        lastName?: string;
        email?: string;
    }>({});

    const validateForm = (): boolean => {
        const newErrors: typeof errors = {};

        if (!formData.firstName.trim()) {
            newErrors.firstName = 'First name is required';
        }

        if (!formData.lastName.trim()) {
            newErrors.lastName = 'Last name is required';
        }

        const emailErr = validate.required(formData.email, 'Email address') ?? validate.email(formData.email);
        if (emailErr) newErrors.email = emailErr;

        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();

        if (!validateForm()) {
            return;
        }

        await onSubmit(formData);

        // Reset form after successful submission
        setFormData({
            firstName: '',
            lastName: '',
            email: '',
            role: 'user',
        });
        setErrors({});
    };

    const handleClose = () => {
        // Reset form when closing
        setFormData({
            firstName: '',
            lastName: '',
            email: '',
            role: 'user',
        });
        setErrors({});
        onClose();
    };

    const handleInputChange = (field: keyof typeof formData, value: string) => {
        setFormData(prev => ({
            ...prev,
            [field]: value
        }));

        // Clear error when user starts typing
        if (errors[field as keyof typeof errors]) {
            setErrors(prev => ({
                ...prev,
                [field]: undefined
            }));
        }
    };

    if (!isOpen) return null;

    return (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-50">
            <div className="bg-white rounded-xl shadow-2xl max-w-md w-full max-h-[90vh] overflow-y-auto">
                {/* Header */}
                <div className="flex items-center justify-between p-6 border-b border-gray-200">
                    <div className="flex items-center gap-3">
                        <div className="p-2 rounded-lg" style={{ backgroundColor: 'rgba(105, 190, 40, 0.1)' }}>
                            <UserPlus className="w-5 h-5" style={{ color: '#69BE28' }} />
                        </div>
                        <div>
                            <h3 className="text-lg font-semibold text-gray-900">Create New User</h3>
                            <p className="text-sm text-gray-500">User will receive a temporary password</p>
                        </div>
                    </div>
                    <button
                        onClick={handleClose}
                        disabled={isCreating}
                        className="p-2 text-gray-400 hover:text-gray-600 rounded-lg hover:bg-gray-100 transition-colors disabled:opacity-50"
                    >
                        <X className="w-5 h-5" />
                    </button>
                </div>

                {/* Form */}
                <form onSubmit={handleSubmit} className="p-6">
                    <div className="space-y-4">
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">First Name *</label>
                            <input
                                type="text"
                                value={formData.firstName}
                                onChange={(e) => handleInputChange('firstName', e.target.value)}
                                className={`w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-offset-2 ${errors.firstName ? 'border-red-500 bg-red-50' : 'border-gray-300'}`}
                                style={{ '--tw-ring-color': 'rgba(105, 190, 40, 0.5)' } as React.CSSProperties}
                                disabled={isCreating}
                            />
                            {errors.firstName && <p className="mt-1 text-sm text-red-600">{errors.firstName}</p>}
                        </div>

                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">Last Name *</label>
                            <input
                                type="text"
                                value={formData.lastName}
                                onChange={(e) => handleInputChange('lastName', e.target.value)}
                                className={`w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-offset-2 ${errors.lastName ? 'border-red-500 bg-red-50' : 'border-gray-300'}`}
                                style={{ '--tw-ring-color': 'rgba(105, 190, 40, 0.5)' } as React.CSSProperties}
                                disabled={isCreating}
                            />
                            {errors.lastName && <p className="mt-1 text-sm text-red-600">{errors.lastName}</p>}
                        </div>

                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">Email Address *</label>
                            <input
                                type="email"
                                value={formData.email}
                                onChange={(e) => handleInputChange('email', e.target.value)}
                                className={`w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-offset-2 ${errors.email ? 'border-red-500 bg-red-50' : 'border-gray-300'}`}
                                style={{ '--tw-ring-color': 'rgba(105, 190, 40, 0.5)' } as React.CSSProperties}
                                placeholder="user@example.com"
                                disabled={isCreating}
                            />
                            {errors.email && <p className="mt-1 text-sm text-red-600">{errors.email}</p>}
                        </div>

                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">Role *</label>
                            <select
                                value={formData.role}
                                onChange={(e) => handleInputChange('role', e.target.value)}
                                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-offset-2"
                                style={{ '--tw-ring-color': 'rgba(105, 190, 40, 0.5)' } as React.CSSProperties}
                                disabled={isCreating}
                            >
                                <option value="user">User</option>
                                <option value="compliance">Compliance</option>
                                <option value="manager">Manager</option>
                                <option value="admin">Admin</option>
                            </select>
                            <p className="mt-1 text-xs text-gray-500">User will be able to change their password on first login</p>
                        </div>
                    </div>

                    {/* Info Box */}
                    <div className="mt-4 p-3 bg-gray-50 border border-gray-200 rounded-lg">
                        <p className="text-sm text-gray-600">
                            <strong>Note:</strong> A temporary password will be automatically generated and displayed after creation. Make sure to securely share it with the user.
                        </p>
                    </div>

                    {/* Footer */}
                    <div className="flex justify-end space-x-3 pt-6 mt-6 border-t border-gray-200">
                        <button
                            type="button"
                            onClick={handleClose}
                            disabled={isCreating}
                            className="px-4 py-2 text-gray-700 border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50"
                        >
                            Cancel
                        </button>
                        <button
                            type="submit"
                            disabled={isCreating}
                            className="px-4 py-2 text-white rounded-lg hover:opacity-90 transition-opacity disabled:opacity-50 flex items-center"
                            style={{ backgroundColor: '#69BE28' }}
                        >
                            {isCreating && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
                            {isCreating ? 'Creating...' : 'Create User'}
                        </button>
                    </div>
                </form>
            </div>
        </div>
    );
};

export default CreateUserModal;